import { Box, CircularProgress } from '@mui/material';
import { TextWrapper } from '../../../../shared/components/formUtilities/useTextWrapper';
import { useTranslation } from 'react-i18next';
import { AbsherService } from '../../../../services/absherService';
import { useEffect, useRef, useState } from 'react';
import {
	getRegistrationData,
	setRegistrationData,
	updateInvestorSteps,
} from '../../../../services/authService';
import { useNavigate } from 'react-router-dom';
import { buildLocalizeRoute } from '../../../../helpers/languageHelper';
import { AppRoutes } from '../../../../constants/appRoutes';
import { Subject, concatMap, interval, takeUntil } from 'rxjs';
import { Toaster } from '../../../../shared/components/common/toaster';
import { ButtonWrapper } from '../../../../shared/components/formUtilities/useButton';
import { InvestorRegService } from '../../investor/investorRegService';
import AbsherLogo from '../../../../assets/images/Absher_logo.svg';

const getInvestorRouteForStep = (step) => {
	if (step === 2) return AppRoutes.IRegPersonalInfo;
	if (step === 3) return AppRoutes.iRegDocumentVarification;
	return AppRoutes.IRegPersonalInfo;
};

export const AbsherVerification = () => {
	const absherService = new AbsherService();
	const investorRegService = new InvestorRegService();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [requesting, setRequesting] = useState(false);
	const [loadingCheck, setLoadingCheck] = useState(false);
	const [failed, setFailed] = useState(false);
	const [stopTimer, setStopTimer] = useState(false);
	const pollingStarted = useRef(false);

	useEffect(() => {
		sendRequest();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const sendRequest = () => {
		setRequesting(true);
		setFailed(false);
		pollingStarted.current = false;
		absherService
			.requestVerification()
			.then(() => {
				setRequesting(false);
			})
			.catch(() => {
				setRequesting(false);
				setFailed(true);
			});
	};

	useEffect(() => {
		if (!requesting && !failed && !pollingStarted.current) {
			pollingStarted.current = true;
			const cleanup = startPolling();
			return () => {
				pollingStarted.current = false;
				cleanup?.();
			};
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [requesting, failed]);

	const afterVerification = () => {
		Toaster({ msg: t('absherVerified'), isError: false });
		investorRegService.getInvestorRegistrationData().then((resp) => {
			const step = resp.data?.step;
			updateInvestorSteps(step);
			setRegistrationData({ ...getRegistrationData(), step });
			navigate(
				buildLocalizeRoute(
					`${AppRoutes.InvestorRegistration}/${getInvestorRouteForStep(step)}`
				)
			);
		});
	};

	const startPolling = () => {
		const stopTimer$ = new Subject();
		const subscription = interval(3000)
			.pipe(
				takeUntil(stopTimer$),
				takeUntil(interval(300000)),
				concatMap(async () => await absherService.getStatus())
			)
			.subscribe({
				next: (resp) => {
					const status = resp.data?.status;
					if (status === 'Completed') {
						subscription.unsubscribe();
						stopTimer$.next();
						stopTimer$.complete();
						afterVerification();
					} else if (status === 'Failed') {
						subscription.unsubscribe();
						stopTimer$.next();
						stopTimer$.complete();
						setFailed(true);
					}
				},
				error: () => {
					subscription.unsubscribe();
				},
				complete: () => {
					setStopTimer(true);
					subscription.unsubscribe();
					stopTimer$.next();
					stopTimer$.complete();
				},
			});

		return () => {
			subscription.unsubscribe();
			stopTimer$.next();
			stopTimer$.complete();
		};
	};

	const onManuallyCheck = () => {
		setLoadingCheck(true);
		absherService
			.getStatus()
			.then((resp) => {
				setLoadingCheck(false);
				const status = resp.data?.status;
				if (status === 'Completed') {
					afterVerification();
				} else if (status === 'Failed') {
					setFailed(true);
				}
			})
			.catch(() => {
				setLoadingCheck(false);
			});
	};

	return (
		<Box className="flex flex-col justify-center items-center reg-section-height">
			<Box className="md:w-[450px] w-full p-6 flex flex-col items-center shadow-md rounded-md">
				<Box className="h-[100px] w-auto">
					<img
						src={AbsherLogo}
						alt="Absher logo"
						className="h-full w-auto"
					/>
				</Box>
				<Box className="flex flex-col items-center pt-6 w-full">
					<TextWrapper
						classList="sub-heading !text-center"
						text={t('absherInfoText')}
					/>
					{!failed ? (
						<Box className="flex justify-center items-center mt-4">
							{!stopTimer ? (
								<Box className="flex justify-center items-center">
									<CircularProgress className="!h-[14px] !w-[14px]" />
									<Box className="w-[6px]"></Box>
									<TextWrapper
										classList="caption !text-sm !text-center"
										text={t('awaitingResponse')}
									/>
								</Box>
							) : (
								<Box className="flex justify-center rounded-md items-center p-[5px] bg-[#ff00000f]">
									<TextWrapper
										classList="caption !text-sm !text-center"
										text={t('absherNoResponse')}
									/>
								</Box>
							)}
						</Box>
					) : (
						<Box className="flex justify-center rounded-md items-center mt-4 p-[5px] bg-[#ff00000f]">
							<TextWrapper
								classList="caption !text-sm !text-center"
								text={t('absherFailed')}
							/>
						</Box>
					)}
					{failed ? (
						<Box className="w-full mt-4 flex justify-center">
							<ButtonWrapper
								loading={requesting}
								largeBtn
								label={t('tryAgain')}
								onClick={sendRequest}
							/>
						</Box>
					) : stopTimer ? (
						<Box className="w-full mt-4 flex justify-center">
							<ButtonWrapper
								loading={loadingCheck}
								largeBtn
								label={t('verify')}
								onClick={onManuallyCheck}
							/>
						</Box>
					) : null}
				</Box>
			</Box>
		</Box>
	);
};
